import { useState } from 'react';
import { isLoading as isLoadingRead, readyData } from '@/lib/readParts';
import { ReadNotice } from '@/components/ui';
import { Loader2, Sparkles, Trash2 } from 'lucide-react';
import { useCampaignStore } from '@/stores';
import { useAuth } from '@/contexts/AuthContext';
import {
  useBetaPricing,
  useAttachBetaPricing,
  useRemoveBetaPricing,
} from '@/hooks/queries';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

function formatAmount(amountOff: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase() || 'USD',
  }).format(amountOff / 100);
}

export function BetaPricingCard() {
  const { user } = useAuth();
  const selectedCampaign = useCampaignStore((s) => s.selectedCampaign);

  const couponRead = useBetaPricing();
  const coupon = readyData(couponRead);
  const isLoading = isLoadingRead(couponRead);
  const { mutateAsync: attach, isPending: attaching } = useAttachBetaPricing();
  const { mutateAsync: remove, isPending: removing } = useRemoveBetaPricing();

  const [couponInput, setCouponInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  if (!user?.isPlatformAdmin || !selectedCampaign) return null;

  const hasActiveSubscription = Boolean(selectedCampaign.subscriptionStatus)
    && selectedCampaign.subscriptionStatus !== 'Trialing'
    && selectedCampaign.subscriptionStatus !== 'Canceled';

  const handleAttach = async () => {
    const trimmed = couponInput.trim();
    if (!trimmed) {
      setError('Enter a Stripe Coupon ID');
      return;
    }
    setError(null);
    try {
      await attach(trimmed);
      setCouponInput('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to attach coupon');
    }
  };

  const handleRemove = async () => {
    setError(null);
    try {
      await remove();
      setConfirmingRemove(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove coupon');
    }
  };

  return (
    <Card className="border-purple-200">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2 text-purple-800">
          <Sparkles className="h-4 w-4" />
          Beta Loyalty Pricing
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          Attach a Stripe Coupon (duration=forever, amount_off) to lock this campaign
          to a fixed monthly rate. The discount is applied to the standard price and
          shown on every invoice.
        </p>

        <ReadNotice read={couponRead} subject="the beta coupon" />
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading...
          </div>
        ) : coupon ? (
          <div className="space-y-3">
            <div className="rounded-md border border-purple-200 bg-purple-50 px-3 py-2">
              <p className="text-sm font-medium text-purple-900">{coupon.name}</p>
              <p className="text-xs text-purple-700 mt-0.5">
                {formatAmount(coupon.amountOff, coupon.currency)} off — {coupon.duration}
              </p>
              <p className="text-xs text-purple-600/70 mt-0.5 font-mono">
                {coupon.couponId}
              </p>
            </div>

            {confirmingRemove ? (
              <div className="space-y-2">
                {hasActiveSubscription && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                    This campaign has an active subscription. Removal from a live
                    subscription is not currently supported and will return an error.
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleRemove}
                    disabled={removing}
                  >
                    {removing ? (
                      <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Removing...</>
                    ) : (
                      'Yes, remove'
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmingRemove(false)}
                    disabled={removing}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmingRemove(true)}
                className="text-red-700 border-red-300 hover:bg-red-50"
              >
                <Trash2 className="mr-2 h-3.5 w-3.5" />
                Remove beta pricing
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="beta-coupon-id" className="text-sm">
              Stripe Coupon ID
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="beta-coupon-id"
                value={couponInput}
                onChange={(e) => setCouponInput(e.target.value)}
                placeholder="e.g. BETA50"
                disabled={attaching}
                className="font-mono"
              />
              <Button
                size="sm"
                onClick={handleAttach}
                disabled={attaching || couponInput.trim().length === 0}
              >
                {attaching ? (
                  <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Attaching...</>
                ) : (
                  'Attach'
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Create the coupon in the Stripe dashboard first, then paste its ID here.
            </p>
          </div>
        )}

        {error && (
          <p className="mt-3 text-sm text-red-600">{error}</p>
        )}
      </CardContent>
    </Card>
  );
}
